// Placeholder icons at 24 px, drawn as strokes bound to the current color.
// Aaron sources the real icons under the same names (Core Assets).
import type { ReactElement, SVGProps } from 'react';

export type IconName =
  | 'menu'
  | 'close'
  | 'calendar'
  | 'caret-down'
  | 'caret-up'
  | 'chevron-right'
  | 'chevron-left'
  | 'arrow-right'
  | 'back'
  | 'star'
  | 'constellation'
  | 'horoscope'
  | 'clear-latest'
  | 'clear-all'
  | 'check'
  | 'lock'
  | 'home'
  | 'fullscreen'
  | 'settings'
  | 'apogee'
  | 'book'
  | 'info'
  | 'completion'
  | 'image'
  | 'link'
  | 'copy'
  | 'phrase'
  | 'quotation'
  | 'category'
  | 'assemblage'
  | 'star-4'
  | 'mark-play'
  | 'mark-check'
  | 'mark-lock';

const paths: Record<IconName, ReactElement> = {
  menu: (
    <>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6L6 18" />,
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17M8 3v4M16 3v4" />
    </>
  ),
  'caret-down': <path d="M7 10l5 5 5-5" />,
  'caret-up': <path d="M7 14l5-5 5 5" />,
  'chevron-right': <path d="M9.5 6l6 6-6 6" />,
  'chevron-left': <path d="M14.5 6l-6 6 6 6" />,
  'arrow-right': <path d="M5 12h14M13 6l6 6-6 6" />,
  back: <path d="M19 12H5M11 6l-6 6 6 6" />,
  star: <path d="M12 3.5l1.6 6.1 6.1 1.6-6.1 1.6L12 19l-1.6-6.2-6.1-1.6 6.1-1.6z" fill="currentColor" stroke="none" />,
  constellation: (
    <>
      <path d="M5 17l5-8 5 4 4-8" />
      <circle cx="5" cy="17" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="10" cy="9" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="15" cy="13" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="19" cy="5" r="1.6" fill="currentColor" stroke="none" />
    </>
  ),
  // The zodiac wheel (Figma Icon / horoscope), vectorized from the sourced
  // png: two rings, twelve houses, a sun with twelve rays, in fine strokes.
  horoscope: (
    <g strokeWidth="0.75">
      <circle cx="12" cy="12" r="10.125" />
      <circle cx="12" cy="12" r="6.375" />
      <path
        strokeLinecap="butt"
        d="M18.375 12L22.125 12M17.521 15.188L20.769 17.062M15.188 17.521L17.062 20.769M12 18.375L12 22.125M8.812 17.521L6.938 20.769M6.479 15.188L3.231 17.062M5.625 12L1.875 12M6.479 8.812L3.231 6.937M8.812 6.479L6.937 3.231M12 5.625L12 1.875M15.188 6.479L17.062 3.231M17.521 8.812L20.769 6.937"
      />
      <circle cx="12" cy="12" r="2.438" />
      <path d="M14.717 12.728L16.347 13.165M13.989 13.989L15.182 15.182M12.728 14.717L13.165 16.347M11.272 14.717L10.835 16.347M10.011 13.989L8.818 15.182M9.283 12.728L7.653 13.165M9.283 11.272L7.653 10.835M10.011 10.011L8.818 8.818M11.272 9.283L10.835 7.653M12.728 9.283L13.165 7.653M13.989 10.011L15.182 8.818M14.717 11.272L16.347 10.835" />
    </g>
  ),
  'clear-latest': <path d="M9 7l-5 5 5 5M4 12h11a4 4 0 0 0 0-8" />,
  'clear-all': (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  lock: (
    <>
      <rect x="5.5" y="10.5" width="13" height="10" rx="2" />
      <path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3" />
    </>
  ),
  home: <path d="M4 11.5L12 5l8 6.5V19a1 1 0 0 1-1 1h-4.5v-5h-5v5H5a1 1 0 0 1-1-1z" />,
  fullscreen: <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />,
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" />
    </>
  ),
  // Icon / apogee in the Figma file (Apogee's level icon): an arch, and an
  // arrow rising beneath its peak.
  apogee: (
    <path d="M3 12.5C3 7.806 7.029 4 12 4s9 3.806 9 8.5M12 20V7M8 11.243 12 7l4.243 4.243" />
  ),
  book: <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5zM4 20.5V5.5M20 18v3H6.5" />,
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5M12 8v.5" />
    </>
  ),
  completion: <path d="M6 21V3.5M6 4.5h11.5l-2.75 4.25L17.5 13H6" />,
  image: (
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <path d="M3.5 16l5-5 4 4 2.5-2.5 5 5" />
      <circle cx="15.5" cy="9" r="1.4" />
    </>
  ),
  link: <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" />,
  copy: (
    <>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 8.5V6A1.5 1.5 0 0 0 14 4.5H6A1.5 1.5 0 0 0 4.5 6v8A1.5 1.5 0 0 0 6 15.5h2.5" />
    </>
  ),
  // The level kinds on the home rows, as Aaron drew them in the Figma file
  // (Icon / phrase, quotation, category, assemblage, apogee).
  // Phrase: a speech bubble.
  phrase: (
    <path d="M3.086 17.509a1.8 1.8 0 0 0-.094-1.167A10 10 0 1 1 7.77 21.06a1.8 1.8 0 0 0-1.1-.091l-3.413.998a.9.9 0 0 1-1.236-1.168z" />
  ),
  // Quotation: a pair of quotation marks.
  quotation: (
    <>
      <path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z" />
      <path d="M5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z" />
    </>
  ),
  // Category: a list of its kind.
  category: (
    <>
      <path d="M9 7h11M9 12h11M9 17h11" />
      <circle cx="4.5" cy="7" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="17" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  // Assemblage (Icon / assemblage): five stars, a square and its centre, joined
  // from the bottom left up, right, down, then to the centre.
  assemblage: (
    <>
      <path d="M4.5 19.5v-15h15v15L12 12" />
      <circle cx="4.5" cy="19.5" r="2.3" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="4.5" r="2.3" fill="currentColor" stroke="none" />
      <circle cx="19.5" cy="4.5" r="2.3" fill="currentColor" stroke="none" />
      <circle cx="19.5" cy="19.5" r="2.3" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="2.3" fill="currentColor" stroke="none" />
    </>
  ),
  // Icon / star-4: the four-point star of the level rows' star count.
  'star-4': <path d="M12 1l2.2 8.8L23 12l-8.8 2.2L12 23l-2.2-8.8L1 12l8.8-2.2z" fill="currentColor" stroke="none" />,
  // A level card's state marks (Aaron, 2026-11-02): the glyph alone, drawn
  // for the inside of a ring; the card draws the ring as a border.
  'mark-play': <path d="M10 8.7v6.6c0 .5.6.8 1 .5l4.8-3.3c.4-.3.4-.8 0-1.1L11 8.2c-.4-.3-1 0-1 .5z" />,
  'mark-check': <path d="M7.8 12.4l2.8 2.8 5.6-5.8" />,
  'mark-lock': (
    <>
      <rect x="8.4" y="11" width="7.2" height="5.6" rx="1.2" />
      <path d="M10 11V9.5a2 2 0 0 1 4 0V11" />
    </>
  ),
};

interface Props extends SVGProps<SVGSVGElement> {
  name: IconName;
  small?: boolean;
}

export function Icon({ name, small, className, ...rest }: Props) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={['icon', small ? 'icon-sm' : '', className ?? ''].join(' ').trim()}
      aria-hidden="true"
      {...rest}
    >
      {paths[name]}
    </svg>
  );
}
